import React, { useContext, useMemo, useState } from "react";
import { useSearchParams } from "../Hooks/useSearchParams";
import { IdentifierMappingContext } from "./IdentifierMappingContext";
import { FetchBoundary } from "../common/FetchBoundary";
import { displayBalance, formatPharmacyGroupName, pharmacyGroupSort } from "../utils/Helpers";
import pluralize from "pluralize";
import { InputAdornment, TextField } from "@mui/material";
import { Search } from "@material-ui/icons";
import { humanizeText } from "../utils/StringFormatters";
import { Badge } from "../common/Badge";
import MixpanelService from "../utils/MixpanelService";

export const PharmacyGroupList = () => {
  const {
    setPharmacyGroupTab,
    pharmacyGroupSearch,
    setPharmacyGroupSearch,
    setActivePharmacyGroup,
    activePharmacyGroup,
    pharmacyGroupsFetcher,
    pharmacyGroups,
  } = useContext(IdentifierMappingContext);

  const [searchParams, setSearchParams] = useSearchParams();

  const handlePharmacyGroup = (group) => {
    setActivePharmacyGroup(group.name);
    setPharmacyGroupTab("pharmacies");
    setSearchParams({ ...searchParams, pharmacy_group: group.name });
  };

  const handleGroupSearch = (e) => {
    setPharmacyGroupSearch(e.target.value);
    setSearchParams({ ...searchParams, pharmacy_group_search: e.target.value });
  };

  return (
    <div className="bg--white border--rounded" style={{ minHeight: "100%" }}>
      <div className="border--bottom border--rounded-top p-3 sticky bg--white">
        <div className="t--500 mb-2">Pharmacy Groups</div>
        <div className="t--sm t--secondary mb-3">Each pharmacy is categorized into a group.</div>
        <TextField
          label=""
          value={pharmacyGroupSearch}
          onChange={handleGroupSearch}
          onClick={() => {
            MixpanelService.track("esp_horizon_pharm_group_search_accessed");
          }}
          onBlur={() => MixpanelService.track("esp_horizon_pharm_group_search_exited")}
          placeholder="Search for Group, ID, Pharmacy"
          size="small"
          className="w--full"
          sx={{
            "& .MuiInputBase-root": {
              height: 36,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </div>
      <FetchBoundary {...pharmacyGroupsFetcher} className="p-3">
        {pharmacyGroupsFetcher.hasFetched && pharmacyGroups.length == 0 ? (
          <div>No pharmacy groups found.</div>
        ) : null}
        {pharmacyGroups.sort(pharmacyGroupSort).map((group) => {
          const badges = ["dea", "hin", "npi"].map((type) => {
            const count = group.identifier_association_count[type] ?? 0;
            return `${displayBalance(count)} ${type.toUpperCase()}`;
          });

          const active = activePharmacyGroup === group.name;

          return (
            <div
              key={group.name}
              className={`pharmacy-group-card p-3 my-2 border--rounded ${active ? "bg--soft-azure border--blue" : ""}`}
              onClick={() => handlePharmacyGroup(group)}
            >
              <div className="t--500 t--md">{formatPharmacyGroupName(group.name)}</div>
              <div className="t--sm t--secondary mt-1 mb-2">
                {group.name === "unassigned"
                  ? "Unknown Pharmacy"
                  : `${group.pharmacy_count} ${pluralize("Pharmacies", group.pharmacy_count)} Assigned`}
              </div>
              <div className="flex align-items-center justify-content-between">
                <div className="flex gap--4 align-items-center">
                  {badges.map((badge) => (
                    <div
                      className={`badge px-2 py-1 border--none shadow-none ${active ? "bg--light-blue t--blue" : "t--navyblue bg--light-gray"} border--rounded`}
                      key={badge}
                    >
                      {badge}
                    </div>
                  ))}
                </div>
                {group.pending_mapping_request_count > 0 ? (
                  <Badge className="bg--white" color="purple">
                    {group.pending_mapping_request_count}
                  </Badge>
                ) : null}
              </div>
            </div>
          );
        })}
      </FetchBoundary>
    </div>
  );
};
